import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import { clamp, isEmpty, isNumber } from "lodash";

import QuantityPlusMinusButton from "core/Components/Buttons/QuantityButton/QuantityPlusMinusButton";
import RoundButton from "core/Components/Buttons/RoundButton";
import CartIcon from "core/Components/Icons/Cart";
import { useAppState } from "core/contexts/AppContext";
import window from "core/includes/window";

import { MAXIMUM_PRODUCT_QUANTITY, MINIMUM_PRODUCT_QUANTITY } from "pages/Components/ProductListPage/includes/includes";

type Props = {
    className?: string,
    product: ProductItem,
    updateItemQuantity: (productId: string, quantity: number) => Promise<void>,
};

const ProductItemCartButton: React.FC<Props> = ({
    className = '',
    product,
    updateItemQuantity
}) => {
    const theme: Theme = useTheme();
    const [appState] = useAppState();
    const [hasLoading, setHasLoading] = useState(false);
    const timeoutHolder = useRef<any>(null);

    const cartItemQuantity = useMemo(() => (
        appState.cart?.products?.find((item: ProductItem) => item.id.toString() === product.id)?.quantity ?? 0
    ), [appState.cart, product.id]);

    const [itemQuantity, setItemQuantity] = useState(cartItemQuantity);

    const itemQuantityHolder = useRef({
        quantity: cartItemQuantity,
        lastValidQuantity: cartItemQuantity || MINIMUM_PRODUCT_QUANTITY,
    });

    const availableMaxQuantity = useMemo(() => (
        Math.min(Math.max(product.inventoryQuantity ?? 0, 0), MAXIMUM_PRODUCT_QUANTITY)
    ), [product.inventoryQuantity]);

    const [minLimit, maxLimit] = useMemo(() => [
        itemQuantity <= 0,
        itemQuantity >= (availableMaxQuantity ?? 0)
    ], [itemQuantity, availableMaxQuantity]);

    const allowForSale = !(!product.availableForSale || availableMaxQuantity <= 0);

    const setItemQuantityHolder = (quantity: number) => {
        itemQuantityHolder.current = {
            quantity,
            lastValidQuantity: isNumber(quantity) ? quantity : itemQuantityHolder.current.lastValidQuantity,
        };
    };

    const onResetValue = useCallback(() => {
        if (!itemQuantity) {
            const q = cartItemQuantity || itemQuantityHolder.current.lastValidQuantity;
            setItemQuantity(q);
            setItemQuantityHolder(q);
        }
    }, [itemQuantity, cartItemQuantity]);

    const handleChange = useCallback((newQuantity: number, timeout: number = 500) => {
        if (timeoutHolder.current) clearTimeout(timeoutHolder.current);

        timeoutHolder.current = window.setTimeout(() => {
            updateItemQuantity(product.id, newQuantity).then(() => setHasLoading(false));
        }, timeout);
    }, [product.id, updateItemQuantity]);

    const getUpdatedQuantity = (newQuantity: number) => (
        clamp(
            newQuantity,
            0,
            availableMaxQuantity,
        )
    );

    const onPlusIconClick = (event: React.MouseEvent) => {
        onResetValue();
        event.stopPropagation();
        if (!maxLimit) {
            const quantity = getUpdatedQuantity(itemQuantityHolder.current.quantity + 1);
            handleChange(quantity);
            setItemQuantity(quantity);
            setItemQuantityHolder(quantity);
        }
    };

    const onAddToCart  = (event: React.MouseEvent) => {
        event.stopPropagation();
        setHasLoading(true);
        if (!maxLimit) {
            updateItemQuantity(product.id, 1).then(() => setHasLoading(false));
            setItemQuantity(1);
            setItemQuantityHolder(1);
        }
    };

    const onMinusIconClick = (event: React.MouseEvent) => {
        onResetValue();
        event.stopPropagation();
        if (!minLimit) {
            const quantity = getUpdatedQuantity(itemQuantityHolder.current.quantity - 1);
            handleChange(quantity);
            setItemQuantity(quantity);
            setItemQuantityHolder(quantity);
        }
    };

    const onCustomQuantity = useCallback((e: FormEvent<HTMLInputElement>) => {
        const value = isEmpty(e.currentTarget.value) ? 0 : parseInt(e.currentTarget.value);
        e.stopPropagation();
        if (!isNaN(value) && value !== itemQuantityHolder.current.quantity) {
            const quantity = Math.min(value, (availableMaxQuantity ?? 0));

            handleChange(quantity);
            setItemQuantity(quantity);
            setItemQuantityHolder(quantity);
        }
    }, [availableMaxQuantity, handleChange]);

    // It resets the quantity when an item is removed from the sidebar cart.
    useEffect(() => {
        if (cartItemQuantity === 0) {
            setItemQuantity(0);
            setItemQuantityHolder(0);
        }
    }, [cartItemQuantity]);

    const cartButtonStyle = css`
        display: flex;
        align-items: center;
        gap: 30px;

        ${theme.breakpoints.down('sm')} {
            gap: 20px;
            font-size: 13px;
        }
    `;

    // Cart icon size
    const cartStyle = css`
        width: 28px;
        height: 28px;

        ${theme.breakpoints.down('sm')} {
            width: 22px;
            height: 22px;
        }
    `;

    return (
        <div className={className}>
            {cartItemQuantity > 0 ? (
                <QuantityPlusMinusButton
                    quantity={itemQuantityHolder.current.quantity}
                    maxQuantity={availableMaxQuantity}
                    minQuantity={0}
                    onPlusIconClick={onPlusIconClick}
                    onMinusIconClick={onMinusIconClick}
                    onCustomQuantity={onCustomQuantity}
                    className={"quantity-button"}
                    backgroundColour={theme.colours.white}
                />
            ) : (
                <RoundButton
                    buttonHeight={49}
                    buttonSidePadding={34}
                    backgroundColour={theme.colours.gold}
                    disabled={!allowForSale}
                    onButtonClick={onAddToCart}
                    forceLoading={hasLoading}
                    className={"add-to-cart-button"}
                >
                    <div css={cartButtonStyle}>
                        <CartIcon css={cartStyle} />
                        Add to cart
                    </div>
                </RoundButton>
            )}
        </div>
    );
};

export default ProductItemCartButton;